<template>
  <DefaultLayout
    :pageTitle="meta.title"
    :pageDescription="meta.description"
    :showFooterCTA="true"
  >
    <div class="infographics-page">
      <PageHeader
        title="Infographics"
        subtitle-text="Visual guides you can download and share — created by the Transpond team."
        class="mb-0 no-submenu"
      ></PageHeader>

      <div class="container-fluid infographics__content">
        <div class="row">
          <div class="col-xs-12">
            <div class="blog__layout">
              <div class="blog__filters" ref="filters">
                <label class="blog__filters-title" for="infographic-search">Search</label>
                <div class="blog__search">
                  <svg class="blog__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
                  <input
                    id="infographic-search"
                    class="blog__search-input"
                    type="search"
                    v-model="searchQuery"
                    placeholder="Search infographics"
                    autocomplete="off"
                  />
                </div>

                <span class="blog__filters-title">Filter by tag</span>
                <ul class="blog__filters-list">
                  <li>
                    <button
                      type="button"
                      class="blog__filters-item"
                      v-bind:class="{ 'blog__filters-item--active': activeTag === null }"
                      v-on:click="setTag(null)"
                    >
                      All
                    </button>
                  </li>
                  <li v-for="tag in tags" v-bind:key="tag">
                    <button
                      type="button"
                      class="blog__filters-item"
                      v-bind:class="{ 'blog__filters-item--active': activeTag === tag }"
                      v-on:click="setTag(tag)"
                    >
                      {{ tag }}
                    </button>
                  </li>
                </ul>
              </div>

              <div class="blog__grid">
                <InfographicCard
                  v-for="item in filteredItems"
                  v-bind:key="item.id"
                  v-bind:item="item"
                ></InfographicCard>
                <p class="blog__empty" v-if="filteredItems.length === 0">
                  No infographics match<span v-if="searchQuery.trim()"> “{{ searchQuery.trim() }}”</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout";
import PageHeader from "@/components/Header/PageHeader";
import InfographicCard from "@/components/Infographics/InfographicCard";
import infographics from "@/data/infographics";

export default {
  name: "InfographicsPage",
  components: {
    DefaultLayout,
    PageHeader,
    InfographicCard,
  },
  data() {
    return {
      items: infographics,
      activeTag: null,
      searchQuery: "",
      meta: {
        title: "Infographics",
        description:
          "Download Transpond infographics on email, SMS, agencies and marketing strategy — then read the matching blog posts.",
      },
    };
  },
  computed: {
    tags() {
      const labels = new Map();

      this.items.forEach((item) => {
        const tag = item && typeof item.topic === "string" ? item.topic.trim() : "";
        if (!tag) return;
        const key = tag.toLowerCase();
        if (!labels.has(key)) labels.set(key, tag);
      });

      return [...labels.values()].sort((a, b) => a.localeCompare(b, "en"));
    },
    filteredItems() {
      const query = this.searchQuery.trim().toLowerCase();

      return this.items.filter((item) => {
        if (this.activeTag && (item.topic || "").toLowerCase() !== this.activeTag.toLowerCase()) {
          return false;
        }

        if (!query) return true;

        return [item.title, item.description, item.topic]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
    },
  },
  mounted() {
    this.fitCategoryScroll();
    this._onFitCategories = () => this.fitCategoryScroll();
    window.addEventListener("resize", this._onFitCategories, { passive: true });
    window.addEventListener("scroll", this._onFitCategories, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this._onFitCategories);
    window.removeEventListener("scroll", this._onFitCategories);
    if (this._fitFrame) cancelAnimationFrame(this._fitFrame);
  },
  methods: {
    fitCategoryScroll() {
      if (this._fitFrame) return;
      this._fitFrame = requestAnimationFrame(() => {
        this._fitFrame = null;
        const filters = this.$refs.filters;
        if (!filters) return;
        const desktop = window.matchMedia("(min-width: 768px)").matches;
        if (!desktop) {
          filters.style.maxHeight = "";
          return;
        }
        const stickyTop = parseFloat(getComputedStyle(filters).top) || 0;
        const top = Math.max(filters.getBoundingClientRect().top, stickyTop);
        filters.style.maxHeight = Math.max(0, window.innerHeight - top) + "px";
      });
    },
    setTag(tag) {
      this.activeTag = this.activeTag === tag ? null : tag;
      this.$nextTick(() => {
        const grid = this.$el.querySelector(".blog__grid");
        if (!grid) return;
        const offset = 96;
        const top = grid.getBoundingClientRect().top;
        if (top < offset) {
          window.scrollTo({
            top: window.scrollY + top - offset,
            behavior: "smooth",
          });
        }
        this.fitCategoryScroll();
      });
    },
  },
};
</script>
